import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, Outlet, useSearchParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Dashboard } from './components/Dashboard';
import { ProjectView } from './components/ProjectView';
import { InboxView } from './components/InboxView';
import { PeopleView } from './components/PeopleView';
import { SettingsView } from './components/SettingsView';
import { LandingPage } from './components/LandingPage';
import { Onboarding } from './components/Onboarding';
import { Auth } from './components/Auth';
import { AuthCallback } from './components/AuthCallback';
import { ResetPassword } from './components/ResetPassword';
import { ReceiverOnboarding } from './components/ReceiverOnboarding';
import { Demo } from './components/Demo';
import { EmailVerificationBanner } from './components/EmailVerificationBanner';
import { Modal, Input, Button, Loading } from './components/UIComponents';
import { Search, ArrowRight } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { fetchUserWorkspaces, fetchWorkspaceProjects, createWorkspace, createProject, validateWorkspaceName, hasWorkspaceForRole } from './services/dataService';
import { listNotifications, createNotification, getNotificationPreferences, markNotificationAsRead, markAllNotificationsAsRead } from './services/notificationService';
import { recordSession } from './services/securityService';
import { sendProjectInviteEmail } from './services/emailService';
import { generateProjectSlug, findProjectBySlug, parseProjectSlug } from './utils/routing';
import {
  UserRole,
  Project,
  ProjectStatus,
  MilestoneStatus,
  Notification,
  Person,
  Comment,
  TeamMember,
  AssigneeVisibility,
  OnboardingQuestion,
  PresenceStatus,
  OnboardingAnswer,
  Attachment,
  Note,
  Workspace
} from './types';

type ViewState = 'auth-callback' | 'reset-password' | 'email-confirmation-pending' | 'receiver-onboarding';

const AppContent: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewState, setViewState] = useState<ViewState | null>(null);
  const [role, setRole] = useState<UserRole>(UserRole.PROVIDER);
  const [dataLoading, setDataLoading] = useState(true); // Start as true to prevent flicker
  const [hasInitialized, setHasInitialized] = useState(false);

  // Workspaces - initialized as empty, loaded from Supabase
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);

  const [currentView, setCurrentView] = useState('projects');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [projects, setProjects] = useState<Project[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationPrefs, setNotificationPrefs] = useState({ comments: true, approvals: true, files: false });
  const [people, setPeople] = useState<Person[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Workspace creation modal state (for role switching)
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
  const [pendingRoleSwitch, setPendingRoleSwitch] = useState<UserRole | null>(null);
  const [workspaceModalName, setWorkspaceModalName] = useState('');
  const [workspaceModalError, setWorkspaceModalError] = useState<string | null>(null);

  // ALL hooks must be called before any conditional returns
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Update role based on workspace
  useEffect(() => {
    const ws = workspaces.find(w => w.id === currentWorkspaceId);
    if (ws) {
      setRole(ws.role);
    }
  }, [currentWorkspaceId, workspaces]);

  const userDisplayName = user?.user_metadata?.name || (user?.email ? user.email.split('@')[0] : 'You');

  // Handle auth callback from URL (email confirmation and password reset)
  // This must be before any conditional returns per Rules of Hooks
  useEffect(() => {
    const hash = window.location.hash;
    // Handle both email confirmation tokens and password reset tokens
    if (hash.includes('access_token') || hash.includes('type=recovery')) {
      setViewState('auth-callback');
      // Don't clean the URL here - AuthCallback component needs to read the hash
      // It will clean up the URL after processing the authentication token
    }
  }, []);

  const mapNotifications = (items: any[]) =>
    (items || []).map((n: any) => ({
      id: n.id,
      type: n.type,
      message: n.message,
      projectId: n.project_id || '',
      isRead: n.is_read ?? false,
      date: n.created_at || new Date().toISOString(),
    }));

  // Load user data from Supabase scoped by role
  const loadUserData = async (userId: string, targetRole?: UserRole) => {
    setDataLoading(true);
    try {
      const allWorkspaces = await fetchUserWorkspaces(userId);
      let activeRole = targetRole ?? role;

      if (!targetRole && allWorkspaces.length > 0) {
        const hasRoleMatch = allWorkspaces.some(ws => ws.role === role);
        if (!hasRoleMatch) {
          activeRole = allWorkspaces[0].role;
        }
      }

      const scopedWorkspaces = allWorkspaces.filter(ws => ws.role === activeRole);

      // Preserve currentWorkspaceId if it still exists in scoped workspaces
      let nextWorkspaceId: string | null = null;
      if (currentWorkspaceId && scopedWorkspaces.some(ws => ws.id === currentWorkspaceId)) {
        nextWorkspaceId = currentWorkspaceId;
      } else {
        nextWorkspaceId = scopedWorkspaces[0]?.id ?? null;
      }

      // Fetch projects for the current workspace only (enforces workspace isolation)
      // Projects will be reloaded when workspace changes via separate useEffect
      const userProjects = nextWorkspaceId ? await fetchWorkspaceProjects(nextWorkspaceId) : [];
      const userNotifications = await listNotifications(userId);
      const prefs = await getNotificationPreferences(userId);

      setRole(activeRole);
      setWorkspaces(scopedWorkspaces);
      setProjects(userProjects);
      setNotifications(mapNotifications(userNotifications));
      setNotificationPrefs(prefs);
      setCurrentWorkspaceId(nextWorkspaceId);
      // Navigation logic is handled by the redirect useEffect
      // Don't set viewState here to avoid race conditions
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Clear all user-specific data
  const clearUserData = () => {
    setWorkspaces([]);
    setProjects([]);
    setNotifications([]);
    setPeople([]);
    setTeamMembers([]);
    setCurrentWorkspaceId(null);
    setSelectedProjectId(null);
    setCurrentView('projects');
    setSearchQuery('');
  };

  // Load user data when authenticated user is available
  useEffect(() => {
    if (!loading && user?.email_confirmed_at) {
      loadUserData(user.id);
    } else if (!loading && !user) {
      // User logged out, clear all data
      clearUserData();
      setDataLoading(false); // Ensure dataLoading is false for unauthenticated users
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  useEffect(() => {
    if (user?.id) {
      recordSession(user.id, typeof navigator !== 'undefined' ? navigator.userAgent : undefined);
    }
  }, [user?.id]);

  // Reload projects when workspace changes (enforces workspace isolation)
  useEffect(() => {
    const reloadProjects = async () => {
      if (!loading && user?.email_confirmed_at && currentWorkspaceId && !dataLoading) {
        try {
          const workspaceProjects = await fetchWorkspaceProjects(currentWorkspaceId);
          setProjects(workspaceProjects);
        } catch (error) {
          console.error('Error reloading projects for workspace:', error);
        }
      }
    };
    reloadProjects();
  }, [currentWorkspaceId, user?.email_confirmed_at, loading, dataLoading]);

  // Initialize: Mark as initialized once auth state is known
  useEffect(() => {
    if (!loading) {
      setHasInitialized(true);
    }
  }, [loading]);

  // Protected Route Component - requires authentication and email confirmation
  const ProtectedRoute: React.FC<{ children: React.ReactNode; requireWorkspace?: boolean }> = ({ children, requireWorkspace = false }) => {
    if (loading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    if (!user) {
      return <Navigate to="/" replace />;
    }

    if (!user.email_confirmed_at) {
      return <Navigate to="/" replace />;
    }

    if (requireWorkspace && !dataLoading && workspaces.length === 0) {
      return <Navigate to="/onboarding" replace />;
    }

    return <>{children}</>;
  };

  // Public Route Component - redirects authenticated users appropriately
  const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Only check loading for auth state, not dataLoading for unauthenticated users
    if (loading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    // If authenticated with confirmed email, redirect based on workspace status
    if (user?.email_confirmed_at) {
      if (dataLoading) {
        return (
          <div className="min-h-screen bg-black flex items-center justify-center">
            <Loading />
          </div>
        );
      }
      if (workspaces.length > 0) {
        return <Navigate to="/dashboard" replace />;
      } else {
        // Authenticated but no workspaces - redirect to onboarding
        return <Navigate to="/onboarding" replace />;
      }
    }

    return <>{children}</>;
  };

  // Auth Route Component - redirects if authenticated
  const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Only check loading for auth state, not dataLoading for unauthenticated users
    if (loading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    if (user?.email_confirmed_at) {
      // For authenticated users, wait for data to load
      if (dataLoading) {
        return (
          <div className="min-h-screen bg-black flex items-center justify-center">
            <Loading />
          </div>
        );
      }
      if (workspaces.length > 0) {
        const defaultRole = workspaces[0].role;
        const rolePath = getRolePath(defaultRole);
        return <Navigate to={`/${rolePath}/dashboard`} replace />;
      } else {
        return <Navigate to="/onboarding" replace />;
      }
    }

    return <>{children}</>;
  };

  // Provider Routes Component - wraps all /provider/* routes
  const ProviderRoutes: React.FC = () => {
    if (loading || dataLoading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (!user.email_confirmed_at) {
      return <Navigate to="/login" replace />;
    }

    // Check if user has a provider workspace
    const hasProviderWorkspace = workspaces.some(ws => ws.role === UserRole.PROVIDER);

    if (!hasProviderWorkspace) {
      // Redirect to default dashboard based on available workspaces
      const defaultRole = workspaces.length > 0 ? workspaces[0].role : UserRole.PROVIDER;
      const rolePath = getRolePath(defaultRole);
      return <Navigate to={`/${rolePath}/dashboard`} replace />;
    }

    // Set role to provider if not already set and set workspace
    if (role !== UserRole.PROVIDER) {
      const providerWorkspace = workspaces.find(ws => ws.role === UserRole.PROVIDER);
      if (providerWorkspace) {
        setRole(UserRole.PROVIDER);
        setCurrentWorkspaceId(providerWorkspace.id);
      }
    } else {
      // Ensure workspace is set for provider role
      const providerWorkspace = workspaces.find(ws => ws.role === UserRole.PROVIDER);
      if (providerWorkspace && currentWorkspaceId !== providerWorkspace.id) {
        setCurrentWorkspaceId(providerWorkspace.id);
      }
    }

    return <Outlet />;
  };

  // Receiver Routes Component - wraps all /receiver/* routes
  const ReceiverRoutes: React.FC = () => {
    if (loading || dataLoading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loading />
        </div>
      );
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (!user.email_confirmed_at) {
      return <Navigate to="/login" replace />;
    }

    // Check if user has a receiver workspace
    const hasReceiverWorkspace = workspaces.some(ws => ws.role === UserRole.RECEIVER);

    if (!hasReceiverWorkspace) {
      // Redirect to default dashboard based on available workspaces
      const defaultRole = workspaces.length > 0 ? workspaces[0].role : UserRole.PROVIDER;
      const rolePath = getRolePath(defaultRole);
      return <Navigate to={`/${rolePath}/dashboard`} replace />;
    }

    // Set role to receiver if not already set and set workspace
    if (role !== UserRole.RECEIVER) {
      const receiverWorkspace = workspaces.find(ws => ws.role === UserRole.RECEIVER);
      if (receiverWorkspace) {
        setRole(UserRole.RECEIVER);
        setCurrentWorkspaceId(receiverWorkspace.id);
      }
    } else {
      // Ensure workspace is set for receiver role
      const receiverWorkspace = workspaces.find(ws => ws.role === UserRole.RECEIVER);
      if (receiverWorkspace && currentWorkspaceId !== receiverWorkspace.id) {
        setCurrentWorkspaceId(receiverWorkspace.id);
      }
    }

    return <Outlet />;
  };

  // Helper function to get role path from UserRole
  const getRolePath = (userRole: UserRole): string => {
    return userRole === UserRole.PROVIDER ? 'provider' : 'receiver';
  };

  // Helper function to get default dashboard URL based on role
  const getDashboardUrl = (userRole: UserRole, view?: string): string => {
    const rolePath = getRolePath(userRole);
    if (view) {
      return `/${rolePath}/dashboard/${view}`;
    }
    return `/${rolePath}/dashboard`;
  };


  // Show loading state while checking auth (after all hooks)
  if (loading && !hasInitialized) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  const activeWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || workspaces[0];

  const handleOnboardingComplete = async (selectedRole: 'PROVIDER' | 'RECEIVER', workspaceName?: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    const roleValue = selectedRole === 'PROVIDER' ? UserRole.PROVIDER : UserRole.RECEIVER;
    const name = (workspaceName || '').trim() || '';

    // Validate workspace name doesn't conflict with existing workspaces (only if name provided)
    if (name) {
      const validationError = await validateWorkspaceName(user.id, name, roleValue);
      if (validationError) {
        throw new Error(validationError);
      }
    }

    // createWorkspace will generate unique name if name is empty
    const createdWorkspace = await createWorkspace(user.id, name, roleValue);
    setRole(roleValue);
    await loadUserData(user.id, roleValue);
    setCurrentWorkspaceId(createdWorkspace.id);
    navigate(getDashboardUrl(roleValue));
  };

  const handleLogout = async () => {
    // Clear all user data before signing out
    clearUserData();
    await signOut();
    navigate('/');
  };

  const handleNavigate = (view: string) => {
    if (view.startsWith('receiver_onboarding_')) {
      setSelectedProjectId(view.split('receiver_onboarding_')[1]);
      setViewState('receiver-onboarding');
      return;
    }
    const rolePath = getRolePath(role);
    if (view === 'projects') {
      navigate(`/${rolePath}/dashboard`);
    } else if (view === 'archive') {
      navigate(`/${rolePath}/dashboard/archive`);
    } else if (view === 'inbox') {
      navigate(`/${rolePath}/dashboard/inbox`);
    } else if (view === 'people') {
      navigate(`/${rolePath}/dashboard/people`);
    } else if (view === 'settings') {
      navigate(`/${rolePath}/dashboard/settings`);
    } else {
      setCurrentView(view);
      if (view !== 'project-detail') setSelectedProjectId(null);
    }
  };

  const handleSelectProject = (id: string) => {
    const project = projects.find(p => p.id === id);
    if (project) {
      const slug = generateProjectSlug(project);
      const rolePath = getRolePath(role);
      navigate(`/${rolePath}/project/${slug}`);
      setIsSearchOpen(false);
    }
  };

  const handleCompleteDraft = (projectId: string) => {
    setEditingProjectId(projectId);
    const rolePath = getRolePath(role);
    navigate(`/${rolePath}/dashboard`);
  };

  const handleCreateProject = async (newProject: Partial<Project>): Promise<Project | undefined> => {
    if (!user || !currentWorkspaceId) return undefined;
    const project = await createProject(user.id, currentWorkspaceId, {
      name: newProject.name || 'Untitled Project',
      receiverName: newProject.receiverName || '',
      receiverEmail: newProject.receiverEmail || '',
      description: newProject.description,
      status: newProject.status || ProjectStatus.ACTIVE,
      startDate: newProject.startDate,
      assigneeVisibility: newProject.assigneeVisibility,
    });
    if (project) {
      setProjects([project, ...projects]);

      // Send invitation email if receiver email is provided (non-blocking)
      if (newProject.receiverEmail && newProject.receiverEmail.trim()) {
        try {
          await sendProjectInviteEmail({
            to: newProject.receiverEmail.trim(),
            projectName: newProject.name || 'Untitled Project',
            projectDescription: newProject.description || '',
          });
          console.log('Project invitation email sent successfully');
        } catch (emailErr) {
          console.error('Error sending project invitation email:', emailErr);
          // Don't block project creation if email fails
        }
      }
      return project;
    }
    return undefined;
  };

  const handleCreateWorkspace = async (name: string) => {
    if (!user) return;
    try {
      // createWorkspace will generate unique name if name is empty
      const created = await createWorkspace(user.id, name || '', role);
      setWorkspaces(prev => [created, ...prev]);
      setCurrentWorkspaceId(created.id);
    } catch (error: any) {
      console.error('Failed to create workspace:', error);
      // Error handling can be added here if needed for workspace creation from dashboard
    }
  };

  const authorLabel = userDisplayName || 'You';
  const currentRoleName = userDisplayName;
  const currentInitial =
    currentRoleName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase() || 'Y';

  const pushNotification = async (type: string, message: string, projectId?: string) => {
    if (!user) return;
    if (type === 'comment' && !notificationPrefs.comments) return;
    if (type === 'approval' && !notificationPrefs.approvals) return;
    if (type === 'file' && !notificationPrefs.files) return;

    const sendEmail = typeof document !== 'undefined' ? !document.hasFocus() : false;
    await createNotification(user.id, { type, message, projectId, sendEmail });
    const updated = await listNotifications(user.id);
    setNotifications(mapNotifications(updated));
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    if (!user) return;
    try {
      await markNotificationAsRead(user.id, notificationId);
      // Update local state
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    if (!user) return;
    try {
      await markAllNotificationsAsRead(user.id);
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDeliverWork = (projectId: string, milestoneId: string, text: string, attachments: Attachment[]) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      const updatedMilestones = p.milestones.map(m => {
        if (m.id !== milestoneId) return m;
        const newComment: Comment = {
          id: Math.random().toString(36).substr(2, 9),
          author: authorLabel,
          role: role,
          text,
          date: new Date().toISOString(),
          isResolved: false,
          attachments: attachments.map(a => ({ ...a, uploadedBy: authorLabel, uploadedAt: new Date().toISOString(), source: 'comment' })),
          isDelivery: true
        };
        return { ...m, status: MilestoneStatus.REVIEW, comments: [...m.comments, newComment] };
      });
      return { ...p, milestones: updatedMilestones, status: ProjectStatus.WAITING_ON_RECEIVER, updatedAt: new Date().toISOString() };
    }));
    pushNotification('comment', 'A new delivery was added', projectId);
  };

  const handleApproveMilestone = (projectId: string, milestoneId: string) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      let nextMilestoneId: string | null = null;
      let foundCurrent = false;
      const updatedMilestones = p.milestones.map((m, idx) => {
        if (m.id === milestoneId) {
          foundCurrent = true;
          if (p.milestones[idx + 1]) nextMilestoneId = p.milestones[idx + 1].id;
          return { ...m, status: MilestoneStatus.APPROVED, completedAt: new Date().toISOString() };
        }
        if (foundCurrent && m.id === nextMilestoneId) return { ...m, status: MilestoneStatus.IN_PROGRESS };
        return m;
      });
      const allComplete = updatedMilestones.every(m => m.status === MilestoneStatus.APPROVED);
      return { ...p, milestones: updatedMilestones, status: allComplete ? ProjectStatus.COMPLETED : ProjectStatus.ACTIVE, updatedAt: new Date().toISOString() };
    }));
    pushNotification('approval', 'Milestone approved', projectId);
  };

  const handleUpdateMilestone = (projectId: string, milestoneId: string, newStatus: MilestoneStatus) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, milestones: p.milestones.map(m => m.id === milestoneId ? { ...m, status: newStatus } : m), updatedAt: new Date().toISOString() } : p));
  };

  const handleAddComment = (projectId: string, milestoneId: string, text: string, attachments?: Attachment[], isDelivery?: boolean) => {
    if (isDelivery) { handleDeliverWork(projectId, milestoneId, text, attachments || []); return; }
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, milestones: p.milestones.map(m => m.id === milestoneId ? { ...m, comments: [...m.comments, { id: Math.random().toString(), author: authorLabel, role: role, text, date: new Date().toISOString(), isResolved: false, attachments: (attachments || []).map(a => ({ ...a, uploadedBy: authorLabel, uploadedAt: new Date().toISOString(), source: 'comment' })) }] } : m) } : p));
    pushNotification('comment', 'New comment posted', projectId);
  };

  const handleAddNote = (projectId: string, title: string, content: string) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, notes: [{ id: Math.random().toString(), title, content, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), author: authorLabel, authorRole: role }, ...p.notes] } : p));
  };

  const handleUpdateNote = (projectId: string, noteId: string, title: string, content: string) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, notes: p.notes.map(n => n.id === noteId ? { ...n, title, content, updatedAt: new Date().toISOString() } : n) } : p));
  };

  const handleDeleteNote = (projectId: string, noteId: string) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, notes: p.notes.filter(n => n.id !== noteId) } : p));
  };

  const handleArchiveProject = (projectId: string) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: ProjectStatus.ARCHIVED } : p));
    const rolePath = getRolePath(role);
    navigate(`/${rolePath}/dashboard`);
  };

  const handleRestoreProject = (projectId: string) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: ProjectStatus.ACTIVE } : p));
    const rolePath = getRolePath(role);
    navigate(`/${rolePath}/dashboard`);
  };

  const handleDeleteProject = (projectId: string) => {
    setProjects(prev => prev.filter(p => p.id !== projectId));
    const rolePath = getRolePath(role);
    navigate(`/${rolePath}/dashboard/archive`);
  };

  const handleBulkDeleteProjects = async (ids: string[]) => {
    setProjects(prev => prev.filter(p => !ids.includes(p.id)));
  };

  const handleBulkRestoreProjects = async (ids: string[]) => {
    setProjects(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: ProjectStatus.ACTIVE } : p));
  };

  const handleUpdateProject = (projectId: string, data: Partial<Project>) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data, updatedAt: new Date().toISOString() } : p));
  };

  const handleUpdateProjectAndSend = async (projectId: string, projectData: Partial<Project>): Promise<Project | undefined> => {
    const existingProject = projects.find(p => p.id === projectId);
    if (!existingProject) return undefined;

    const hasOnboarding = projectData.onboardingQuestions && projectData.onboardingQuestions.length > 0;
    const updatedStatus = hasOnboarding ? ProjectStatus.WAITING_ON_RECEIVER : ProjectStatus.ACTIVE;

    // Update project in state
    const updatedProject: Project = {
      ...existingProject,
      ...projectData,
      status: updatedStatus,
      updatedAt: new Date().toISOString()
    };

    setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));

    // Send invitation email if receiver email is provided (non-blocking)
    if (projectData.receiverEmail && projectData.receiverEmail.trim()) {
      try {
        await sendProjectInviteEmail({
          to: projectData.receiverEmail.trim(),
          projectName: projectData.name || existingProject.name,
          projectDescription: projectData.description || existingProject.description || '',
        });
        console.log('Project invitation email sent successfully');
      } catch (emailErr) {
        console.error('Error sending project invitation email:', emailErr);
        // Don't block project update if email fails
      }
    }

    return updatedProject;
  };

  const handleUpdateProjectQuestions = (projectId: string, questions: OnboardingQuestion[]) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, onboardingQuestions: questions, updatedAt: new Date().toISOString() } : p));
  };

  const handleAddPerson = (newPerson: Partial<Person>) => {
    const person: Person = {
      ...newPerson,
      id: Math.random().toString(36).substr(2, 9),
      sharedProjectsCount: 0
    } as Person;
    setPeople([...people, person]);
  };

  const handleInviteMember = (email: string) => {
    const newMember: TeamMember = {
      id: Math.random().toString(36).substr(2, 9),
      name: email.split('@')[0],
      initials: email.substring(0, 2).toUpperCase(),
      avatarColor: 'bg-zinc-700',
      role: 'Collaborator',
      status: 'Pending',
      email: email,
      presence: PresenceStatus.OFFLINE
    };
    setTeamMembers([...teamMembers, newMember]);

    const newPerson: Person = {
      id: newMember.id,
      name: newMember.name,
      email: email,
      initials: newMember.initials,
      avatarColor: 'bg-zinc-700',
      role: 'Collaborator',
      relationshipStatus: 'Invited',
      sharedProjectsCount: 1
    };
    setPeople([...people, newPerson]);

    return newMember.id;
  };

  const handleReceiverOnboardingComplete = (answers: OnboardingAnswer[]) => {
    if (!selectedProjectId) return;
    const project = projects.find(p => p.id === selectedProjectId);
    setProjects(prev => prev.map(p => p.id === selectedProjectId ? { ...p, status: ProjectStatus.DRAFT, startDate: new Date().toISOString(), onboardingAnswers: answers, updatedAt: new Date().toISOString() } : p));
    setRole(UserRole.RECEIVER);
    if (project) {
      const slug = generateProjectSlug(project);
      navigate(`/receiver/project/${slug}`);
    } else {
      navigate('/receiver/dashboard');
    }
  };

  const handleWorkspaceDeleted = async () => {
    if (!user) return;
    // Reload user data to get updated workspace list and projects
    // loadUserData will automatically set currentWorkspaceId to the first available workspace
    // If deleted workspace was current, user will be switched to another workspace
    await loadUserData(user.id, role);

    // Navigate to dashboard to ensure clean state after workspace deletion
    const rolePath = getRolePath(role);
    navigate(`/${rolePath}/dashboard`);
  };

  const handleFinalizeProject = (projectId: string) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;

      const updatedMilestones = p.milestones.map((m, idx) => ({
        ...m,
        status: idx === 0 ? MilestoneStatus.IN_PROGRESS : MilestoneStatus.NOT_STARTED
      }));

      return {
        ...p,
        status: ProjectStatus.ACTIVE,
        milestones: updatedMilestones,
        updatedAt: new Date().toISOString()
      };
    }));
    pushNotification('system', 'Timeline finalized — project activated', projectId);
  };

  // Projects are already filtered by workspace at the data layer (fetchWorkspaceProjects)
  // No need for additional filtering here
  const workspaceProjects = projects;

  const filteredSearchProjects = workspaceProjects.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.receiverName.toLowerCase().includes(searchQuery.toLowerCase()));

  // Visible projects - Dashboard will filter by archive/active status
  const visibleProjects = workspaceProjects;

  // Extract view from URL pathname
  const getViewFromUrl = (pathname: string, rolePath: string): string => {
    if (pathname === `/${rolePath}/dashboard` || pathname === `/${rolePath}/dashboard/projects`) {
      return 'projects';
    } else if (pathname === `/${rolePath}/dashboard/archive`) {
      return 'archive';
    } else if (pathname === `/${rolePath}/dashboard/inbox`) {
      return 'inbox';
    } else if (pathname === `/${rolePath}/dashboard/people`) {
      return 'people';
    } else if (pathname === `/${rolePath}/dashboard/settings`) {
      return 'settings';
    } else if (pathname.startsWith(`/${rolePath}/project/`)) {
      return 'project-detail';
    }
    return 'projects';
  };

  // Dashboard shell component - renders Layout with content
  const DashboardShell = () => {
    const rolePath = getRolePath(role);
    const currentUrlView = getViewFromUrl(location.pathname, rolePath);

    // Ref to cache the previous project to stabilize the reference and prevent flicker
    const previousProjectRef = useRef<{ id: string; notesHash: string; project: Project } | null>(null);

    // Stabilize project reference to prevent flicker - only update when notes actually change
    // This memoization prevents unnecessary re-renders of ProjectView when only the object reference changes
    const projectFromUrl = useMemo(() => {
      // Extract project from URL
      let rawProject: Project | undefined = undefined;
      if (currentUrlView === 'project-detail') {
        const pathMatch = location.pathname.match(new RegExp(`/${rolePath}/project/([^/]+)`));
        if (pathMatch && pathMatch[1]) {
          rawProject = findProjectBySlug(pathMatch[1], projects);
        }
      }

      if (!rawProject) {
        previousProjectRef.current = null;
        return undefined;
      }

      const notesHash = JSON.stringify(rawProject.notes?.map(n => ({ id: n.id, title: n.title, content: n.content })) || []);

      // If same project and same notes, return cached reference to prevent re-render
      if (
        previousProjectRef.current?.id === rawProject.id &&
        previousProjectRef.current?.notesHash === notesHash
      ) {
        return previousProjectRef.current.project;
      }

      // Notes changed - update cache and return new reference
      previousProjectRef.current = {
        id: rawProject.id,
        notesHash,
        project: rawProject
      };

      return rawProject;
    }, [currentUrlView, location.pathname, rolePath, projects]);

    let content;
    if (currentUrlView === 'projects' || currentUrlView === 'archive') {
      content = <Dashboard
        projects={visibleProjects}
        onSelectProject={handleSelectProject}
        onCreateProject={handleCreateProject}
        role={role}
        autoOpenCreate={false}
        teamMembers={teamMembers}
        onBulkDeleteProjects={handleBulkDeleteProjects}
        onBulkRestoreProjects={handleBulkRestoreProjects}
        editingProject={editingProjectId ? projects.find(p => p.id === editingProjectId) : undefined}
        onUpdateProjectAndSend={handleUpdateProjectAndSend}
        onCloseEdit={() => setEditingProjectId(null)}
      />;
    } else if (currentUrlView === 'project-detail' && projectFromUrl) {
      const project = projectFromUrl;
      const backUrl = project.status === ProjectStatus.ARCHIVED
        ? `/${rolePath}/dashboard/archive`
        : `/${rolePath}/dashboard`;
      content = <ProjectView project={project} role={role} teamMembers={teamMembers} onBack={() => navigate(backUrl)} onUpdateMilestone={handleUpdateMilestone} onAssignMilestone={(pid, mid, uid) => { }} onInviteMember={handleInviteMember} onUpdateProjectStatus={(pid, s) => { }} onUpdateAssigneeVisibility={(pid, v) => { }} onUpdateProjectQuestions={handleUpdateProjectQuestions} onUpdateMilestoneTitle={(pid, mid, t) => { }} onAddComment={handleAddComment} onResolveComment={(pid, mid, cid) => { }} onDeleteProject={handleDeleteProject} onArchiveProject={handleArchiveProject} onRestoreProject={handleRestoreProject} onOpenReceiverView={() => handleNavigate(`client_onboarding_${project.id}`)} onDeliverWork={handleDeliverWork} onApproveMilestone={handleApproveMilestone} onAddNote={handleAddNote} onUpdateNote={handleUpdateNote} onDeleteNote={handleDeleteNote} onAddProjectFile={(pid, f) => { }} onFinalizeProject={() => handleFinalizeProject(project.id)} onUpdateProject={handleUpdateProject} onCompleteDraft={handleCompleteDraft} />;
    } else if (currentUrlView === 'inbox') {
      content = <InboxView notifications={notifications} projects={projects} onSelect={(n) => handleSelectProject(n.projectId)} onMarkAsRead={handleMarkNotificationAsRead} onMarkAllAsRead={handleMarkAllNotificationsAsRead} />;
    } else if (currentUrlView === 'people') {
      content = <PeopleView people={people} role={role} onAddPerson={handleAddPerson} projects={projects} onSelectProject={handleSelectProject} />;
    } else if (currentUrlView === 'settings') {
      const initials = currentRoleName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || 'Y';
      const currentUser = { name: currentRoleName, email: user?.email || '', initials, avatarColor: 'bg-zinc-700' };
      content = <SettingsView role={role} user={currentUser} workspace={activeWorkspace} onWorkspaceDeleted={handleWorkspaceDeleted} allWorkspaces={workspaces} />;
    }

    // Handle receiver-onboarding as special case (triggered from within dashboard)
    if (viewState === 'receiver-onboarding' && selectedProjectId) {
      const project = projects.find(p => p.id === selectedProjectId);
      if (project) return <ReceiverOnboarding project={project} onComplete={handleReceiverOnboardingComplete} />;
    }

    // Map URL view to Layout view name
    const layoutView = currentUrlView === 'project-detail' ? 'projects' : currentUrlView;

    return (
      <>
        {user && <EmailVerificationBanner />}
        <Layout
          currentRole={role}
          currentView={layoutView}
          onNavigate={handleNavigate}
          onSwitchRole={handleSwitchRole}
          onLogout={handleLogout}
          currentPresence={PresenceStatus.ACTIVE}
          onOpenSearch={() => setIsSearchOpen(true)}
          theme={theme}
          onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
          userName={currentRoleName}
          userInitial={currentInitial}
          workspaces={workspaces}
          currentWorkspaceId={currentWorkspaceId || undefined}
          onSwitchWorkspace={setCurrentWorkspaceId}
          onCreateWorkspace={handleCreateWorkspace}
        >
          {content}
          <Modal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} title="Search">
            <div className="p-4">
              <div className="flex items-center gap-2 border-b border-zinc-200 dark:border-[#2A2A2A] pb-2 mb-4">
                <Search size={16} className="text-zinc-500 dark:text-[#555555]" />
                <input autoFocus className="w-full bg-transparent text-zinc-900 dark:text-[#EDEDED] outline-none placeholder-zinc-400 dark:placeholder-zinc-600" placeholder="Search projects..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {filteredSearchProjects.map(p => (
                  <button key={p.id} onClick={() => handleSelectProject(p.id)} className="w-full flex justify-between px-3 py-2 hover:bg-zinc-100 dark:hover:bg-[#1E1E1E] rounded text-sm text-zinc-800 dark:text-[#EDEDED] transition-colors">
                    <span>{p.name}</span><span className="text-zinc-500 dark:text-[#555555]">{p.receiverName}</span>
                  </button>
                ))}
              </div>
            </div>
          </Modal>
        </Layout>
      </>
    );
  };


  const handleSwitchRole = async () => {
    if (!user) return;
    const nextRole = role === UserRole.PROVIDER ? UserRole.RECEIVER : UserRole.PROVIDER;

    // Check if user has a workspace for the target role
    const hasWorkspace = await hasWorkspaceForRole(user.id, nextRole);

    if (!hasWorkspace) {
      // Show modal to create workspace for the new role
      setPendingRoleSwitch(nextRole);
      setWorkspaceModalName('');
      setWorkspaceModalError(null);
      setIsWorkspaceModalOpen(true);
      return;
    }

    // Switch role if workspace exists
    setRole(nextRole);
    setCurrentWorkspaceId(null);
    setSelectedProjectId(null);
    setCurrentView('projects');
    await loadUserData(user.id, nextRole);
    navigate(getDashboardUrl(nextRole));
  };

  const handleWorkspaceModalSubmit = async () => {
    if (!user || !pendingRoleSwitch) return;

    const name = workspaceModalName.trim() || '';

    // Validate workspace name only if provided
    if (name) {
      const validationError = await validateWorkspaceName(user.id, name, pendingRoleSwitch);
      if (validationError) {
        setWorkspaceModalError(validationError);
        // Auto-focus input on error
        setTimeout(() => {
          const input = document.querySelector('#workspace-modal-input') as HTMLInputElement;
          if (input) input.focus();
        }, 100);
        return;
      }
    }

    // Create workspace - will generate unique name if name is empty
    try {
      const createdWorkspace = await createWorkspace(user.id, name, pendingRoleSwitch);
      setWorkspaces(prev => [createdWorkspace, ...prev]);
      setRole(pendingRoleSwitch);
      setCurrentWorkspaceId(createdWorkspace.id);
      setSelectedProjectId(null);
      setCurrentView('projects');
      setIsWorkspaceModalOpen(false);
      setPendingRoleSwitch(null);
      setWorkspaceModalName('');
      setWorkspaceModalError(null);
      await loadUserData(user.id, pendingRoleSwitch);
      navigate(getDashboardUrl(pendingRoleSwitch));
    } catch (error: any) {
      setWorkspaceModalError(error.message || 'Failed to create workspace. Please try again.');
    }
  };

  const handleWorkspaceModalClose = () => {
    setIsWorkspaceModalOpen(false);
    setPendingRoleSwitch(null);
    setWorkspaceModalName('');
    setWorkspaceModalError(null);
  };

  // Handle auth-callback separately (uses URL hash)
  // Check URL hash for auth tokens before rendering routes
  const hash = window.location.hash;
  const hasAuthToken = hash.includes('access_token') || hash.includes('type=recovery');

  if (hasAuthToken) {
    return <AuthCallback
      onComplete={() => navigate('/login')}
      onError={() => navigate('/login')}
      onRecovery={() => navigate('/reset-password')}
    />;
  }

  return (
    <>
      {/* Auth Modal (Login/Signup) */}
      <Modal
        isOpen={!!searchParams.get('auth')}
        onClose={() => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('auth');
          setSearchParams(newParams);
        }}
        title=""
        maxWidth="max-w-md"
        noPadding={true}
      >
        <div className="bg-zinc-950 p-8">
          <Auth
            mode={searchParams.get('auth') === 'signup' ? 'signup' : 'login'}
            onComplete={() => {
              // Clear auth param
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('auth');
              setSearchParams(newParams);
              // Navigation will be handled by the auth state change in other effects or components
            }}
            onSwitchMode={() => {
              const currentMode = searchParams.get('auth');
              const newMode = currentMode === 'login' ? 'signup' : 'login';
              const newParams = new URLSearchParams(searchParams);
              newParams.set('auth', newMode);
              setSearchParams(newParams);
            }}
            isModal={true}
          />
        </div>
      </Modal>

      {/* Workspace Creation Modal for Role Switch */}
      <Modal
        isOpen={isWorkspaceModalOpen}
        onClose={handleWorkspaceModalClose}
        title={`Create ${pendingRoleSwitch === UserRole.PROVIDER ? 'Provider' : 'Receiver'} Workspace`}
        maxWidth="max-w-md"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-500">
            You're switching to {pendingRoleSwitch === UserRole.PROVIDER ? 'Provider' : 'Receiver'} view.
            Please create a workspace for this role.
          </p>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-medium block">
              Workplace name (Optional)
            </label>
            <Input
              id="workspace-modal-input"
              placeholder={pendingRoleSwitch === UserRole.RECEIVER ? 'Receiver company or team name' : 'Provider / studio / personal brand'}
              value={workspaceModalName}
              onChange={(e) => {
                setWorkspaceModalName(e.target.value);
                setWorkspaceModalError(null);
              }}
              className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-zinc-700"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleWorkspaceModalSubmit();
                }
              }}
            />
            <p className="text-xs text-zinc-600">
              Default: "My Workspace"
            </p>
          </div>

          {workspaceModalError && (
            <div className="text-red-400 text-sm bg-red-950/20 border border-red-900/30 p-3 rounded">
              {workspaceModalError}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button
              variant="ghost"
              onClick={handleWorkspaceModalClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleWorkspaceModalSubmit}
              size="lg"
            >
              Create Workspace <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </Modal>

      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicRoute>
            <LandingPage
              onLogin={() => setSearchParams({ auth: 'login' })}
              onSignup={() => setSearchParams({ auth: 'signup' })}
              onDemo={() => navigate('/demo')}
            />
          </PublicRoute>
        } />

        <Route path="/login" element={<Navigate to="/?auth=login" replace />} />
        <Route path="/signup" element={<Navigate to="/?auth=signup" replace />} />

        <Route path="/reset-password" element={
          <ResetPassword
            onComplete={() => navigate('/login')}
            onError={() => navigate('/login')}
          />
        } />

        {/* Demo Route - allows exploring the app without authentication */}
        <Route path="/demo/*" element={<Demo />} />

        {/* Protected Routes */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding onComplete={handleOnboardingComplete} />
          </ProtectedRoute>
        } />

        {/* Legacy routes - redirect to role-based routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute requireWorkspace={true}>
            <Navigate to={`/${getRolePath(workspaces.length > 0 ? workspaces[0].role : UserRole.PROVIDER)}/dashboard`} replace />
          </ProtectedRoute>
        } />

        <Route path="/projects/new" element={
          <ProtectedRoute requireWorkspace={true}>
            <Navigate to={`/${getRolePath(workspaces.length > 0 ? workspaces[0].role : UserRole.PROVIDER)}/projects/new`} replace />
          </ProtectedRoute>
        } />

        {/* Role-based routes */}
        <Route path="/provider/*" element={
          <ProtectedRoute requireWorkspace={true}>
            <ProviderRoutes />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<DashboardShell />} />
          <Route path="dashboard/archive" element={<DashboardShell />} />
          <Route path="dashboard/inbox" element={<DashboardShell />} />
          <Route path="dashboard/people" element={<DashboardShell />} />
          <Route path="dashboard/settings" element={<DashboardShell />} />
          <Route path="project/:projectSlug" element={<DashboardShell />} />
          <Route path="projects/new" element={<DashboardShell />} />
        </Route>

        <Route path="/receiver/*" element={
          <ProtectedRoute requireWorkspace={true}>
            <ReceiverRoutes />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<DashboardShell />} />
          <Route path="dashboard/archive" element={<DashboardShell />} />
          <Route path="dashboard/inbox" element={<DashboardShell />} />
          <Route path="dashboard/people" element={<DashboardShell />} />
          <Route path="dashboard/settings" element={<DashboardShell />} />
          <Route path="project/:projectSlug" element={<DashboardShell />} />
          <Route path="projects/new" element={<DashboardShell />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;